import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Clock, UserCheck, FileSignature, Wallet, Crown, GraduationCap, Quote, Trophy, Zap, Timer, Rocket, Sparkles, HeartPulse, HardHat, Fuel, Truck, Cog, Briefcase, Landmark, Shirt, Hammer, Ship, ConciergeBell, Laptop } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { Reveal, Overline } from "../Reveal";
import { CountUp } from "../CountUp";
import { FoundationIcon, AwardMedalIcon, HandshakeIcon, GlobeExpandIcon } from "./AwardIcons";

const SVC_IMGS = ["svc-temporary", "svc-direct", "svc-contract", "svc-payrolling", "svc-executive", "svc-training"];
const SVC_ICONS = [Clock, UserCheck, FileSignature, Wallet, Crown, GraduationCap];

export function Solutions() {
  const { t } = useLang();
  const items = t("solutions.items");
  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("solutions.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("solutions.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4 text-lg">{t("solutions.sub")}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {items.map((s, i) => {
            const Icon = SVC_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`solution-card-${i}`} className="card-light overflow-hidden group h-full">
                  <div className="relative h-44 overflow-hidden">
                    <img src={`/assets/${SVC_IMGS[i]}.jpg`} alt={s.t} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E]/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 start-4 w-11 h-11 rounded-xl bg-[#E8842C] flex items-center justify-center shadow-lg group-hover:rotate-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-head font-bold text-lg text-[#1B2A4E] group-hover:text-[#E8842C] transition-colors">{s.t}</h3>
                    <p className="text-sm text-[#1B2A4E]/60 mt-2 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const STAFF_ICONS = [Timer, Zap, Rocket];

export function StaffingServices() {
  const { t } = useLang();
  const items = t("staffing.items");
  return (
    <section id="staffing" className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="staffing-section">
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("staffing.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("staffing.title")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {items.map((s, i) => {
            const Icon = STAFF_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`staffing-card-${i}`} className="card-light p-8 h-full group">
                  <div className="w-14 h-14 rounded-2xl bg-[#1B2A4E] flex items-center justify-center mb-6 group-hover:bg-[#E8842C] group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-head font-bold text-xl text-[#1B2A4E] mb-3">{s.t}</h3>
                  <p className="text-sm text-[#1B2A4E]/60 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const IND_ICONS = [HeartPulse, HardHat, Fuel, Truck, Cog, Briefcase, Landmark, Shirt, Hammer, Ship, ConciergeBell, Laptop];
const IND_KEYS = ["healthcare", "construction", "oilgas", "transport", "mechanical", "business", "finance", "garment", "fabrication", "shipbuilding", "hospitality", "it"];

export function Industries() {
  const { t } = useLang();
  return (
    <section className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="industries-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("industries.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("industries.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4 text-lg">{t("industries.sub")}</p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mt-14">
          {IND_KEYS.map((k, i) => {
            const Icon = IND_ICONS[i];
            return (
              <Reveal key={k} delay={i * 0.04}>
                <div data-testid={`industry-card-${k}`} className="group relative rounded-2xl border border-[#1B2A4E]/10 bg-[#FAF7F2] h-40 sm:h-48 p-5 flex flex-col justify-between overflow-hidden hover:border-[#E8842C]/60 hover:shadow-xl hover:shadow-[#E8842C]/10 hover:-translate-y-1 transition-all duration-300">
                  <span className="absolute -end-6 -top-6 w-24 h-24 rounded-full bg-[#E8842C]/0 group-hover:bg-[#E8842C]/10 transition-colors duration-500" />
                  <span className="absolute end-4 top-4 font-head font-black text-4xl text-[#1B2A4E]/5 group-hover:text-[#E8842C]/15 transition-colors" dir="ltr">{String(i + 1).padStart(2, "0")}</span>
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
                    className="relative w-14 h-14 rounded-2xl bg-white border border-[#1B2A4E]/10 shadow-md shadow-[#1B2A4E]/10 flex items-center justify-center group-hover:bg-[#E8842C] group-hover:border-[#E8842C] group-hover:rotate-6 group-hover:scale-110 transition-all duration-300"
                  >
                    <span className="absolute inset-0 rounded-2xl border-2 border-dashed border-[#E8842C]/0 group-hover:border-[#E8842C]/40 group-hover:scale-125 transition-all duration-500" />
                    <Icon className="w-7 h-7 text-[#E8842C] group-hover:text-white transition-colors duration-300" strokeWidth={1.8} />
                  </motion.div>
                  <p className="font-head font-bold text-[#1B2A4E] text-sm sm:text-base leading-snug group-hover:text-[#E8842C] transition-colors">{t(`categories.${k}`)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const TESTI_IMGS = ["testi-moataz", "testi-fahad", "testi-abdullah", "testi-nayaf", "testi-ahmet"];

export function Testimonials() {
  const { t } = useLang();
  const testis = t("testis");
  return (
    <section id="testimonials" className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Overline>{t("testisec.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E] mb-14">{t("testisec.title")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testis.map((tm, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div data-testid={`testimonial-${i}`} className="card-light p-7 h-full flex flex-col">
                <Quote className="w-7 h-7 text-[#E8842C] mb-4" />
                <p className="text-sm text-[#1B2A4E]/75 leading-relaxed flex-1">"{tm.q}"</p>
                <div className="mt-6 pt-4 border-t border-[#1B2A4E]/10 flex items-center gap-3">
                  <img src={`/assets/${TESTI_IMGS[i]}.jpg`} alt={tm.n} loading="lazy" className="w-12 h-12 rounded-full object-cover border-2 border-[#E8842C]" />
                  <div>
                    <p className="font-bold text-[#1B2A4E] text-sm">{tm.n}</p>
                    <p className="text-xs text-[#E8842C] font-bold mt-0.5">{tm.c}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const AWARD_ICONS = [FoundationIcon, AwardMedalIcon, HandshakeIcon, GlobeExpandIcon];

const AwardCard = ({ a, i }) => {
  const [play, setPlay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5 });
  const Icon = AWARD_ICONS[i];
  useEffect(() => {
    if (!inView) return;
    setPlay((p) => p + 1);
    const id = setInterval(() => setPlay((p) => p + 1), 6500);
    return () => clearInterval(id);
  }, [inView]);
  return (
    <div ref={ref} data-testid={`award-${i}`} onMouseEnter={() => setPlay((p) => p + 1)} onClick={() => setPlay((p) => p + 1)} className="card-light p-7 text-center h-full group relative overflow-hidden">
      <span className="absolute -top-10 -end-10 w-32 h-32 rounded-full bg-[#E8842C]/5 group-hover:bg-[#E8842C]/15 group-hover:scale-125 transition-all duration-500" />
      <div className="relative w-24 h-24 mx-auto mb-6">
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 rounded-full border-2 border-dashed border-[#E8842C]/40"
        />
        <span className="absolute inset-2 rounded-full bg-[#1B2A4E] shadow-xl shadow-[#1B2A4E]/25 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
          <Icon key={play} />
        </span>
        <span className="absolute -top-1 -end-1 w-8 h-8 rounded-full bg-[#F5A623] border-4 border-white flex items-center justify-center shadow-md">
          {i === 1 ? <Trophy className="w-3.5 h-3.5 text-white" /> : <Sparkles className="w-3.5 h-3.5 text-white" />}
        </span>
      </div>
      <span className="inline-block bg-[#1B2A4E] text-white text-[11px] font-black px-3 py-1 rounded-full tracking-wider" dir="ltr">{a.year}</span>
      <h3 className="font-head font-bold text-[#1B2A4E] mt-3 mb-2 leading-snug group-hover:text-[#E8842C] transition-colors">{a.t}</h3>
      <p className="text-xs text-[#1B2A4E]/60 leading-relaxed">{a.d}</p>
    </div>
  );
};

export function Awards() {
  const { t } = useLang();
  const awards = t("awards");
  return (
    <section className="relative py-28 px-6 sm:px-12 dot-grid" data-testid="awards-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("awardssec.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("awardssec.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4 text-lg">{t("awardssec.sub")}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {awards.map((a, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <AwardCard a={a} i={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CLIENT_LOGOS = Array.from({ length: 36 }, (_, i) => `/assets/clients2/l${String(i + 1).padStart(2, "0")}.png`);
const ROW_A = CLIENT_LOGOS.slice(0, 18);
const ROW_B = CLIENT_LOGOS.slice(18);

const LogoRow = ({ logos, reverse, offset }) => {
  const row = [...logos, ...logos];
  return (
    <div className="flex w-max items-center logo-row" style={{ animation: `${reverse ? "marquee-rev" : "marquee"} 55s linear infinite` }}>
      {row.map((src, i) => (
        <motion.div
          key={i}
          data-testid={i < logos.length ? `client-logo-${offset + i}` : undefined}
          animate={{ y: [0, i % 2 ? -5 : 5, 0] }}
          transition={{ duration: 4 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: (i % 5) * 0.3 }}
          className="mx-3 w-44 h-24 shrink-0 rounded-2xl bg-white border border-[#1B2A4E]/8 shadow-md shadow-[#1B2A4E]/5 flex items-center justify-center p-4 hover:border-[#E8842C]/60 hover:shadow-xl hover:shadow-[#E8842C]/15 hover:-translate-y-1.5 hover:scale-105 transition-all duration-300 group"
        >
          <img src={src} alt="client" loading="lazy" className="max-w-full max-h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
        </motion.div>
      ))}
    </div>
  );
};

const CLIENT_STATS = [
  { to: 500, suffix: "+", key: "companies" },
  { to: 25, suffix: "+", key: "years" },
  { to: 12, suffix: "+", key: "industries" },
];

export function Clients() {
  const { t } = useLang();
  return (
    <section className="relative py-24 px-0 overflow-hidden bg-white border-y border-[#1B2A4E]/8" data-testid="clients-section">
      <div className="absolute inset-0 dot-grid opacity-60" />
      <div className="absolute -top-32 start-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#E8842C]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
          <Reveal>
            <Overline>{t("clientssec.overline")}</Overline>
            <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("clientssec.title")}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-3 gap-px bg-[#1B2A4E]/10 rounded-2xl overflow-hidden border border-[#1B2A4E]/10" data-testid="clients-stats">
              {CLIENT_STATS.map((s) => (
                <div key={s.key} className="bg-[#FAF7F2] px-4 py-5 text-center">
                  <div className="font-head font-black text-2xl sm:text-3xl text-[#E8842C]" dir="ltr"><CountUp to={s.to} suffix={s.suffix} /></div>
                  <div className="text-[10px] uppercase tracking-widest text-[#1B2A4E]/50 mt-1 font-bold">{t(`stats.${s.key}`)}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
      <div className="relative mt-14 space-y-5 py-4" dir="ltr" style={{ maskImage: "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)" }}>
        <LogoRow logos={ROW_A} offset={0} />
        <LogoRow logos={ROW_B} reverse offset={18} />
      </div>
    </section>
  );
}
